// useLayoutInspectorWidth.js

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  STUDIO_LAYOUT_INSPECTOR_DEFAULT_WIDTH,
  STUDIO_LAYOUT_INSPECTOR_MAX_WIDTH,
  STUDIO_LAYOUT_INSPECTOR_MIN_WIDTH,
  STUDIO_PANEL_WIDTHS_STORAGE_KEY,
} from 'studio/constants/studioLayout';

function readStoredPanelWidths() {
  try {
    const raw = window.localStorage.getItem(STUDIO_PANEL_WIDTHS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function clampInspectorWidth(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return STUDIO_LAYOUT_INSPECTOR_DEFAULT_WIDTH;
  return Math.min(
    STUDIO_LAYOUT_INSPECTOR_MAX_WIDTH,
    Math.max(STUDIO_LAYOUT_INSPECTOR_MIN_WIDTH, Math.round(numericValue)),
  );
}

function readStoredInspectorWidth() {
  const stored = readStoredPanelWidths();
  return clampInspectorWidth(stored?.layoutInspector);
}

function writeStoredInspectorWidth(layoutInspectorWidth) {
  try {
    const stored = readStoredPanelWidths();
    window.localStorage.setItem(STUDIO_PANEL_WIDTHS_STORAGE_KEY, JSON.stringify({
      ...stored,
      layoutInspector: clampInspectorWidth(layoutInspectorWidth),
    }));
  } catch {
    // Ignore quota / privacy mode errors.
  }
}

/**
 * useLayoutInspectorWidth
 *
 * Manages resizable layout inspector width with localStorage persistence.
 */
export default function useLayoutInspectorWidth() {
  const [inspectorWidth, setInspectorWidth] = useState(readStoredInspectorWidth);
  const isResizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(STUDIO_LAYOUT_INSPECTOR_DEFAULT_WIDTH);
  const latestInspectorWidthRef = useRef(inspectorWidth);

  useEffect(() => {
    latestInspectorWidthRef.current = inspectorWidth;
  }, [inspectorWidth]);

  const handleResizeMouseMove = useCallback((event) => {
    if (!isResizingRef.current) return;

    const deltaX = resizeStartXRef.current - event.clientX;
    const nextWidth = clampInspectorWidth(resizeStartWidthRef.current + deltaX);
    setInspectorWidth(nextWidth);
  }, []);

  const handleResizeMouseUp = useCallback(() => {
    if (!isResizingRef.current) return;

    isResizingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', handleResizeMouseMove);
    window.removeEventListener('mouseup', handleResizeMouseUp);

    writeStoredInspectorWidth(latestInspectorWidthRef.current);
  }, [handleResizeMouseMove]);

  const handleResizeMouseDown = useCallback((event) => {
    event.preventDefault();

    isResizingRef.current = true;
    resizeStartXRef.current = event.clientX;
    resizeStartWidthRef.current = latestInspectorWidthRef.current;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleResizeMouseMove);
    window.addEventListener('mouseup', handleResizeMouseUp);
  }, [handleResizeMouseMove, handleResizeMouseUp]);

  useEffect(() => () => {
    window.removeEventListener('mousemove', handleResizeMouseMove);
    window.removeEventListener('mouseup', handleResizeMouseUp);
  }, [handleResizeMouseMove, handleResizeMouseUp]);

  return {
    handleResizeMouseDown,
    inspectorWidth,
  };
}
